import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { tradingService } from '../../../services/tradingService';
import PlotlyChart from '../../../components/charts/PlotlyChart';

const COLORS = [
  '#c9a96e', '#6366f1', '#22c55e', '#ef4444', '#3b82f6',
  '#f59e0b', '#ec4899', '#14b8a6', '#8b5cf6', '#f97316',
];
// Cycle through dash styles so overlapping lines remain distinguishable
const DASHES = ['solid', 'dot', 'dash', 'longdash', 'dashdot', 'longdashdot'];

const fmtPct = (v) => (v != null ? (v * 100).toFixed(1) + '%' : 'N/A');
const fmtNum = (v) => (v != null ? (typeof v === 'number' ? v.toFixed(2) : v) : 'N/A');
const valColor = (v) => (v > 0 ? 'text-green-400' : v < 0 ? 'text-red-400' : 'text-gray-400');
const isPctCol = (c) => {
  const l = c.toLowerCase();
  return l.includes('return') || l.includes('drawdown') || l.includes('vol')
    || l.includes('std') || l.includes('%') || l.includes('best') || l.includes('worst');
};

// ─── Strategy Detail Modal ───────────────────────────────────────────────────

const StrategyDetailModal = ({ stratName, meta, tableData, onClose }) => {
  const [backtest,     setBacktest]     = useState(null);
  const [strategy,     setStrategy]     = useState(null);
  const [stratDef,     setStratDef]     = useState(null); // from available-strategies endpoint
  const [fetchErr,     setFetchErr]     = useState('');
  const [loadingBt,    setLoadingBt]    = useState(false);

  // Fetch strategy record + backtest detail + strategy definition (docstring, entry/exit rules)
  useEffect(() => {
    setLoadingBt(true);
    setFetchErr('');

    const calls = [
      meta?.strategy_id ? tradingService.getStrategy(meta.strategy_id).catch(() => null) : Promise.resolve(null),
      meta?.strategy_id ? tradingService.getBacktests(meta.strategy_id).catch(() => null) : Promise.resolve(null),
      tradingService.getAvailableStrategies().catch(() => null),
    ];

    Promise.all(calls).then(([stratRes, btRes, availRes]) => {
      if (stratRes?.data) setStrategy(stratRes.data);

      const bts = btRes?.data || [];
      const bt  = meta?.backtest_id
        ? (bts.find(b => b.id === meta.backtest_id) || bts[0])
        : bts[0];
      if (bt) setBacktest(bt);

      // Match strategy class to available-strategies definition
      if (availRes?.data && meta?.strategy_class) {
        const def = availRes.data.find(s => s.name === meta.strategy_class);
        if (def) setStratDef(def);
      }
    }).catch(() => setFetchErr('Could not load full details'))
      .finally(() => setLoadingBt(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.strategy_id, meta?.backtest_id, meta?.strategy_class]);

  // Params: prefer live strategy.parameters, fall back to metadata
  const params = strategy?.parameters || meta?.parameters_dict || {};

  // Equity curve traces (absolute equity, not % — shows $ growth)
  const equityTraces = useMemo(() => {
    const ec = backtest?.equity_curve || [];
    if (!ec.length) return [];
    return [{
      x: ec.map(p => p.date),
      y: ec.map(p => p.equity),
      type: 'scatter', mode: 'lines',
      name: 'Equity',
      line: { color: '#c9a96e', width: 1.5 },
      fill: 'tozeroy',
      fillcolor: 'rgba(201,169,110,0.08)',
      hovertemplate: '$%{y:,.0f}<extra></extra>',
    }];
  }, [backtest]);

  // Stats row for this strategy from the summary table
  const ri = tableData ? tableData.index.indexOf(stratName) : -1;
  const statsRow = ri >= 0 ? tableData.rows[ri] : null;

  // Strategy class display name
  const stratClass = meta?.strategy_class || strategy?.strategy_class || '';
  const classLabel = (stratDef?.display_name || stratClass).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Description: prefer custom strategy's stored description, then module/class docstrings from endpoint
  const description = (strategy?.description || stratDef?.description || '').trim() || null;
  // Entry/exit rules: prefer rules computed with ACTUAL saved parameters (from strategy endpoint),
  // fall back to defaults from available-strategies (which use default params)
  const entryRules  = (strategy?.entry_rules?.length  ? strategy.entry_rules  : stratDef?.entry_rules)  || [];
  const exitRules   = (strategy?.exit_rules?.length   ? strategy.exit_rules   : stratDef?.exit_rules)   || [];
  const paramDescs  = stratDef?.parameter_descriptions || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 md:p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] bg-white dark:bg-[#0d1117] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
          <div className="min-w-0 pr-4">
            <h2 className="font-serif text-base md:text-lg text-gray-900 dark:text-gray-100 leading-snug break-words">{stratName}</h2>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {classLabel && (
                <span className="text-[10px] px-2 py-0.5 bg-brand-gold/15 text-brand-gold rounded-full font-medium uppercase tracking-wide">
                  {classLabel}
                </span>
              )}
              {meta?.symbol && (
                <span className="text-[10px] px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 rounded-full font-mono">
                  {meta.symbol}
                </span>
              )}
              {meta?.months != null && (
                <span className="text-[10px] text-gray-400">{meta.months} months of data</span>
              )}
              {strategy?.is_active && (
                <span className="text-[10px] px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">Live</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none shrink-0 mt-0.5"
          >✕</button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-6">

          {fetchErr && (
            <p className="text-xs text-red-400">{fetchErr}</p>
          )}

          {/* How it works */}
          {description && (
            <div>
              <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">How it works</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">{description}</p>
            </div>
          )}

          {/* Entry / Exit rules */}
          {(entryRules.length > 0 || exitRules.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {entryRules.length > 0 && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Entry Rules</h3>
                  <ul className="space-y-1">
                    {entryRules.map((r, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                        <span className="text-green-400 mt-0.5 shrink-0">▸</span>{r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {exitRules.length > 0 && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Exit Rules</h3>
                  <ul className="space-y-1">
                    {exitRules.map((r, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                        <span className="text-red-400 mt-0.5 shrink-0">▸</span>{r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Parameters */}
          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Parameters</h3>
            {Object.keys(params).length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {Object.entries(params).map(([k, v]) => (
                  <div key={k} className="bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
                    <div className="text-[10px] text-gray-400 mb-0.5 truncate" title={k}>{k.replace(/_/g, ' ')}</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{String(v)}</div>
                    {paramDescs[k] && (
                      <div className="text-[10px] text-gray-500 mt-0.5 leading-snug">{paramDescs[k]}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No parameters stored</p>
            )}
          </div>

          {/* Performance metrics */}
          {statsRow && tableData && (
            <div>
              <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Performance Metrics</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {tableData.columns.map((col, ci) => {
                  const val = statsRow[ci];
                  const formatted = val == null ? 'N/A' : isPctCol(col) ? fmtPct(val) : fmtNum(val);
                  return (
                    <div key={col} className="bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
                      <div className="text-[10px] text-gray-400 mb-0.5 truncate" title={col}>{col}</div>
                      <div className={`text-sm font-semibold tabular-nums ${val != null ? valColor(val) : 'text-gray-400'}`}>{formatted}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Equity curve */}
          {loadingBt ? (
            <div className="py-6 text-center text-gray-400 text-sm">Loading backtest details…</div>
          ) : equityTraces.length > 0 ? (
            <div>
              <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Equity Curve</h3>
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#161b22] p-3">
                <PlotlyChart
                  data={equityTraces}
                  layout={{
                    margin: { t: 10, b: 30, l: 60, r: 10 },
                    yaxis: { tickprefix: '$', tickformat: ',.0f', gridcolor: '#374151' },
                    xaxis: { showgrid: false },
                    paper_bgcolor: 'transparent',
                    plot_bgcolor:  'transparent',
                    font: { color: '#9ca3af', size: 10 },
                    showlegend: false,
                  }}
                  style={{ height: 200 }}
                />
              </div>
            </div>
          ) : null}

          {/* Trade log */}
          {backtest?.trades?.length > 0 && (
            <div>
              <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
                Trade Log — {backtest.trades.length} trades
                {backtest.trades.length > 50 && <span className="text-gray-500"> (showing first 50)</span>}
              </h3>
              <div className="overflow-x-auto scrollbar-thin rounded-xl border border-gray-200 dark:border-gray-800 max-h-56 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800/50 sticky top-0 z-10">
                    <tr className="text-[10px] uppercase tracking-wider text-gray-400">
                      <th className="px-3 py-2 text-left">Entry Date</th>
                      <th className="px-3 py-2 text-left">Exit Date</th>
                      <th className="px-3 py-2 text-right">Entry $</th>
                      <th className="px-3 py-2 text-right">Exit $</th>
                      <th className="px-3 py-2 text-right">Return</th>
                      <th className="px-3 py-2 text-right">Bars</th>
                      <th className="px-3 py-2 text-left">Exit Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                    {backtest.trades.slice(0, 50).map((t, i) => {
                      const ret = t.return_pct;
                      const isOpen = !t.exit_date;
                      return (
                        <tr key={i} className={`hover:bg-gray-50 dark:hover:bg-gray-800/30 ${isOpen ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}>
                          <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{t.entry_date?.slice(0, 10) || '—'}</td>
                          <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{isOpen ? <span className="text-blue-400 text-[10px] font-medium">OPEN</span> : t.exit_date?.slice(0, 10)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                            {t.entry_price != null ? `$${Number(t.entry_price).toFixed(2)}` : '—'}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                            {!isOpen && t.exit_price != null ? `$${Number(t.exit_price).toFixed(2)}` : '—'}
                          </td>
                          <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${
                            ret == null ? 'text-gray-400' : ret >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {ret != null ? `${ret >= 0 ? '+' : ''}${(ret * 100).toFixed(1)}%` : '—'}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{t.bars_held ?? '—'}</td>
                          <td className="px-3 py-1.5 text-gray-400 text-[10px]">{t.exit_reason || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const OverviewPage = ({ analyticsParams, paramsKey, focus, onLabelsLoaded, basket, onTogglePin, onDataLoaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [selectedStrategy, setSelectedStrategy] = useState(null);

  useEffect(() => {
    if (!analyticsParams) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [summaryRes, cumulativeRes] = await Promise.all([
          tradingService.analyticsSummary(analyticsParams),
          tradingService.analyticsCumulative(analyticsParams),
        ]);
        if (!cancelled) {
          const loaded = {
            summary: summaryRes.data,
            cumulative: cumulativeRes.data,
          };
          setData(loaded);
          // Populate focus strategy dropdown
          if (onLabelsLoaded && summaryRes.data?.table?.index) {
            onLabelsLoaded(summaryRes.data.table.index.filter(n => n !== 'SPX Index'));
          }
          // Lift data up so parent can pass to PortfolioBuilder
          if (onDataLoaded) onDataLoaded(loaded);
        }
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  // Metric cards
  const metrics = useMemo(() => {
    if (!data?.summary?.table) return null;
    const { index: strategies, columns, data: rows } = data.summary.table;

    const nonBenchmark = strategies.filter((s) => s !== 'SPX Index' && s !== 'SPY');
    const count = nonBenchmark.length;

    const findCol = (keyword) => columns.findIndex((c) => c.toLowerCase().includes(keyword.toLowerCase()));
    const sharpeIdx = findCol('sharpe');
    const returnIdx = findCol('return');
    const ddIdx = findCol('drawdown');

    let medianSharpe = 'N/A';
    let bestCagr = { name: 'N/A', val: -Infinity };
    let worstDd = { name: 'N/A', val: 0 };

    const sharpeVals = [];
    nonBenchmark.forEach((name) => {
      const ri = strategies.indexOf(name);
      if (ri < 0) return;
      const row = rows[ri];

      if (sharpeIdx >= 0 && row[sharpeIdx] != null) sharpeVals.push(row[sharpeIdx]);
      if (returnIdx >= 0 && row[returnIdx] != null && row[returnIdx] > bestCagr.val) {
        bestCagr = { name, val: row[returnIdx] };
      }
      if (ddIdx >= 0 && row[ddIdx] != null && row[ddIdx] < worstDd.val) {
        worstDd = { name, val: row[ddIdx] };
      }
    });

    if (sharpeVals.length > 0) {
      sharpeVals.sort((a, b) => a - b);
      const mid = Math.floor(sharpeVals.length / 2);
      medianSharpe = sharpeVals.length % 2 === 0
        ? ((sharpeVals[mid - 1] + sharpeVals[mid]) / 2).toFixed(2)
        : sharpeVals[mid].toFixed(2);
    }

    return [
      { label: '# Strategies', value: count },
      { label: 'Median Sharpe', value: medianSharpe, color: valColor(parseFloat(medianSharpe)) },
      { label: 'Best CAGR', value: fmtPct(bestCagr.val === -Infinity ? null : bestCagr.val), sub: bestCagr.name, color: 'text-green-400' },
      { label: 'Worst Max DD', value: fmtPct(worstDd.val === 0 ? null : worstDd.val), sub: worstDd.name, color: 'text-red-400' },
    ];
  }, [data]);

  // Summary table with sorting
  const tableData = useMemo(() => {
    if (!data?.summary?.table) return null;
    const { index, columns, data: rows } = data.summary.table;

    let sortedIndices = index.map((_, i) => i);
    if (sortCol != null) {
      const ci = columns.indexOf(sortCol);
      if (ci >= 0) {
        sortedIndices.sort((a, b) => {
          const va = rows[a][ci], vb = rows[b][ci];
          if (va == null && vb == null) return 0;
          if (va == null) return 1;
          if (vb == null) return -1;
          return sortAsc ? va - vb : vb - va;
        });
      }
    }

    return { index, columns, rows, sortedIndices };
  }, [data, sortCol, sortAsc]);

  // Cumulative chart traces
  const cumTraces = useMemo(() => {
    const cum = data?.cumulative?.cumulative || data?.cumulative;
    if (!cum?.index || !cum?.columns || !cum?.data) return [];
    const { index: dates, columns: names, data: rows } = cum;

    const traces = (names || []).map((name, ci) => {
      const xArr = [], yArr = [];
      dates.forEach((d, i) => {
        if (rows[i][ci] != null) { xArr.push(d); yArr.push(rows[i][ci]); }
      });
      return {
        x: xArr, y: yArr, type: 'scatter', mode: 'lines', name,
        line: {
          color: COLORS[ci % COLORS.length],
          width: 1.5,
          dash: DASHES[ci % DASHES.length],
        },
        hovertemplate: `${name}: %{y:.2%}<extra></extra>`,
      };
    });

    // Add SPY benchmark as dashed red overlay
    const spx = data?.cumulative?.spx;
    if (spx?.index && spx?.columns && spx?.data) {
      const xArr = [], yArr = [];
      spx.index.forEach((d, i) => {
        if (spx.data[i]?.[0] != null) { xArr.push(d); yArr.push(spx.data[i][0]); }
      });
      if (xArr.length > 0) {
        traces.push({
          x: xArr, y: yArr, type: 'scatter', mode: 'lines',
          name: spx.columns[0] || 'SPY',
          line: { color: '#DC143C', width: 2, dash: 'dash' },
          hovertemplate: `SPY: %{y:.2%}<extra></extra>`,
        });
      }
    }

    return traces;
  }, [data]);

  const handleSort = (col) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(true); }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data -- run some backtests first</div>;

  return (
    <div>
      {/* Metric Cards */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {metrics.map((m) => (
            <div key={m.label} className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{m.label}</p>
              <p className={`text-xl font-bold tabular-nums ${m.color || 'text-gray-800 dark:text-gray-100'}`}>{m.value}</p>
              {m.sub && <p className="text-xs text-gray-400 truncate mt-0.5">{m.sub}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Cumulative Returns Chart */}
      {cumTraces.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Cumulative Returns</h3>
          <PlotlyChart
            data={cumTraces}
            layout={{
              paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
              font: { color: '#9ca3af', size: 10 },
              yaxis: { title: 'Cumulative Return', tickformat: '.0%' },
              xaxis: { title: '' },
              legend: { orientation: 'h', yanchor: 'bottom', y: -0.25, xanchor: 'center', x: 0.5 },
            }}
            style={{ minHeight: 350 }}
          />
        </div>
      )}

      {/* Summary Stats Table */}
      {tableData && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Summary Statistics</h3>
          <div className="overflow-x-auto scrollbar-thin rounded-lg border border-gray-200 dark:border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  {onTogglePin && <th className="px-2 py-2.5 sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/50 w-6" />}
                  <th className="px-3 py-2.5 text-left sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/50">Strategy</th>
                  <th className="px-3 py-2.5 text-left bg-gray-50 dark:bg-gray-800/50">Parameters</th>
                  {tableData.columns.map((col) => (
                    <th key={col} className="px-3 py-2.5 text-right whitespace-nowrap cursor-pointer hover:text-brand-gold" onClick={() => handleSort(col)}>
                      {col} {sortCol === col ? (sortAsc ? '\u25B2' : '\u25BC') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {tableData.sortedIndices.map((ri) => {
                  const stratName = tableData.index[ri];
                  const meta = data?.summary?.metadata?.[stratName] || {};
                  const isBenchmark = stratName === 'SPX Index' || stratName === 'SPY';
                  const isPinned = basket?.has(stratName);
                  return (
                  <tr
                    key={stratName}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800/30 ${!isBenchmark ? 'cursor-pointer' : ''}`}
                    onClick={() => { if (!isBenchmark) setSelectedStrategy(stratName); }}
                  >
                    {onTogglePin && (
                      <td className="px-2 py-2 sticky left-0 bg-white dark:bg-[#161b22] w-6" onClick={e => e.stopPropagation()}>
                        {!isBenchmark && (
                          <button
                            onClick={() => onTogglePin(stratName)}
                            title={isPinned ? 'Remove from Portfolio Builder' : 'Add to Portfolio Builder'}
                            className={`text-base leading-none transition-colors ${
                              isPinned ? 'opacity-100' : 'opacity-20 hover:opacity-70'
                            }`}
                          >
                            📌
                          </button>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap text-gray-800 dark:text-gray-200 sticky left-0 bg-white dark:bg-[#161b22]">
                      <span className={!isBenchmark ? 'hover:text-brand-gold transition-colors' : ''}>{stratName}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-400 max-w-[200px] truncate" title={meta.parameters || ''}>
                      {meta.parameters || '-'}
                    </td>
                    {tableData.rows[ri].map((val, ci) => {
                      const formatted = val == null ? 'N/A' : isPctCol(tableData.columns[ci]) ? fmtPct(val) : fmtNum(val);
                      return (
                        <td key={ci} className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${val != null ? valColor(val) : 'text-gray-400'}`}>
                          {formatted}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Strategy Detail Modal */}
      {selectedStrategy && (
        <StrategyDetailModal
          stratName={selectedStrategy}
          meta={data?.summary?.metadata?.[selectedStrategy] || {}}
          tableData={tableData}
          onClose={() => setSelectedStrategy(null)}
        />
      )}
    </div>
  );
};

export default OverviewPage;
